// Tiny fetch helper for the hub REST API. nginx (prod) and the Vite dev proxy
// both forward /api/* to the hub on 127.0.0.1:4100, so we always speak relative
// URLs and never have to think about CORS or absolute hosts.
//
// All requests use `credentials: "same-origin"` (the default for same-origin
// fetches anyway) so the session cookie rides along.

export interface HealthResponse {
  status: "ok" | "degraded" | "error";
  db: "connected" | "down";
  redis: "connected" | "down";
  version: string;
  timestamp: string;
}

export interface ApiUser {
  id: string;
  email: string;
  name: string | null;
  isAdmin: boolean;
}

export interface AdminUser extends ApiUser {
  status: "active" | "suspended";
  createdAt: string;
}

export interface Invitation {
  id: string;
  email: string;
  name: string | null;
  isAdmin: boolean;
  status: "pending" | "accepted" | "expired";
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
  acceptUrl: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message?: string,
  ) {
    super(message ?? code);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (res.status === 204) return undefined as T;
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const errBody = body as { error?: string; message?: string } | null;
    throw new ApiError(res.status, errBody?.error ?? `http_${res.status}`, errBody?.message);
  }
  return body as T;
}

// ----- Health -----
export const getHealth = () => request<HealthResponse>("/api/v1/health");

// ----- Auth -----
export const signIn = (email: string, password: string) =>
  request<{ user: ApiUser }>("/api/v1/auth/sign-in", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });

export const signOut = () => request<void>("/api/v1/auth/sign-out", { method: "POST" });

export const getMe = () => request<{ user: ApiUser }>("/api/v1/auth/me");

export const getInvitation = (token: string) =>
  request<{ email: string; name: string | null }>(`/api/v1/auth/invitation/${encodeURIComponent(token)}`);

export const acceptInvite = (token: string, password: string, name?: string) =>
  request<{ user: ApiUser }>("/api/v1/auth/accept-invite", {
    method: "POST",
    body: JSON.stringify({ token, password, ...(name ? { name } : {}) }),
  });

// ----- Events (per-user) -----
export interface EventCount {
  total: number;
  confirmed: number;
}

export const getEventCount = () => request<EventCount>("/api/v1/events/count");

// ----- Connectors (per-user) -----
export type ConnectorType = "graph" | "google" | "outlook_pst" | "ios" | "android";
export type ConnectorStatus =
  | "awaiting_approval"
  | "approved"
  | "active"
  | "paused"
  | "error"
  | "rejected";

export interface Connector {
  id: string;
  type: ConnectorType;
  name: string | null;
  email: string | null;
  status: ConnectorStatus;
  lastSyncAt: string | null;
  lastError: string | null;
  createdAt: string;
  requestedAt: string | null;
  approvedAt: string | null;
  adminNotes: string | null;
}

export const listConnectors = () =>
  request<{ connectors: Connector[] }>("/api/v1/connectors");

export const requestGoogleConnector = (email: string) =>
  request<{ id: string; email: string; status: string }>(
    "/api/v1/connectors/google/request",
    { method: "POST", body: JSON.stringify({ email }) },
  );

export const deleteConnector = (id: string) =>
  request<void>(`/api/v1/connectors/${encodeURIComponent(id)}`, { method: "DELETE" });

/** OAuth start endpoints 302 to the provider. The SPA does a full-page
 *  navigation, not fetch, so we just expose the URL builder. */
export function googleOAuthStartUrl(connectorId: string, redirectTo: string): string {
  return `/api/v1/connectors/google/oauth/start?connector_id=${encodeURIComponent(connectorId)}&redirect_to=${encodeURIComponent(redirectTo)}`;
}

// ----- Admin: connector approvals -----
export interface ConnectorRequest {
  id: string;
  type: ConnectorType;
  email: string | null;
  status: ConnectorStatus;
  requestedAt: string | null;
  approvedAt: string | null;
  adminNotes: string | null;
  user: { id: string; email: string; name: string | null };
  approvedByEmail: string | null;
}

export const listConnectorRequests = () =>
  request<{ requests: ConnectorRequest[] }>("/api/v1/admin/connector-requests");

export const approveConnectorRequest = (id: string) =>
  request<void>(`/api/v1/admin/connector-requests/${encodeURIComponent(id)}/approve`, {
    method: "POST",
  });

export const rejectConnectorRequest = (id: string, reason?: string) =>
  request<void>(`/api/v1/admin/connector-requests/${encodeURIComponent(id)}/reject`, {
    method: "POST",
    body: JSON.stringify({ reason: reason ?? "" }),
  });

// ----- Admin -----
export const listUsers = () => request<{ users: AdminUser[] }>("/api/v1/admin/users");

export const listInvitations = () =>
  request<{ invitations: Invitation[] }>("/api/v1/admin/invitations");

export const createInvitation = (params: { email: string; name?: string; isAdmin?: boolean }) =>
  request<{ email: string; acceptUrl: string; expiresAt: string }>(
    "/api/v1/admin/invitations",
    { method: "POST", body: JSON.stringify(params) },
  );

export const deleteUser = (id: string) =>
  request<void>(`/api/v1/admin/users/${encodeURIComponent(id)}`, { method: "DELETE" });
